import React from 'react';
import { cssMap, cx } from '@compiled/react';

import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';

import type { Approver } from '@confluence/approvals/entry-points/ApprovalsContext.ts';

import { ProfileAvatarFacepile } from './ProfileAvatarFacepile.tsx';

const styles = cssMap({
	approvalStatusCard: {
		position: 'relative',
	},
	approvalStatusButton: {
		position: 'absolute',
		inset: token('space.0'),
		borderRadius: token('radius.medium'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		color: token('color.text'),
		backgroundColor: token('color.background.neutral.subtle'),
		width: '100%',
		height: '100%',

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	approvalStatusButtonWarning: {
		borderRadius: token('radius.medium'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		backgroundColor: token('color.background.warning'),

		'&:hover': {
			backgroundColor: token('color.background.warning.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.warning.pressed'),
		},
	},
	visualRow: {
		position: 'relative',
		padding: token('space.200'),
		pointerEvents: 'none',
	},
	approvalStatusContainer: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
	},
	fullWidth: {
		width: '100%',
	},
	chevronContainer: {},
});

type ApprovalStatusButtonAppearance = 'default' | 'warning';

export interface ApprovalStatusButtonProps {
	children: React.ReactNode;
	onClick?: () => void;
	approvers?: Approver[];
	ariaLabel: string;
	appearance?: ApprovalStatusButtonAppearance;
}

export const ApprovalStatusButton = ({
	children,
	onClick,
	approvers = [],
	ariaLabel,
	appearance = 'default',
}: ApprovalStatusButtonProps): React.JSX.Element => {
	return (
		<Box xcss={cx(styles.fullWidth, styles.approvalStatusContainer)}>
			<Box xcss={cx(styles.approvalStatusCard, styles.fullWidth)}>
				<Pressable
					aria-label={ariaLabel}
					xcss={cx(
						styles.approvalStatusButton,
						appearance === 'warning' && styles.approvalStatusButtonWarning,
					)}
					onClick={onClick}
					testId="approval-status-button"
				/>
				<Flex
					direction="row"
					xcss={cx(styles.fullWidth, styles.visualRow)}
					justifyContent="space-between"
					alignItems="center"
					gap="space.200"
				>
					{children}
					<Flex gap="space.150" alignItems="center">
						<ProfileAvatarFacepile approvers={approvers} onPointerClick={onClick} />
						<Flex
							alignItems="center"
							justifyContent="center"
							direction="column"
							xcss={styles.chevronContainer}
						>
							<ChevronRightIcon label="" />
						</Flex>
					</Flex>
				</Flex>
			</Box>
		</Box>
	);
};
